import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { Button, Drawer, message } from "antd";
import React, { useEffect, useState } from "react";
import { webLogoAndImages } from "../Assets/svg";
import { getDecryptedUserData } from "../helpers/bycrypt";

export const NoInternetDrawer: React.FC = () => {
  const isNative = Capacitor.isNativePlatform();
  const [isOffline, setIsOffline] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  useEffect(() => {
    if (isOffline) {
      document.body.classList.add("no-internet-at-tablet");
    } else {
      document.body.classList.remove("no-internet-at-tablet");
    }
    return () => {
      document.body.classList.remove("no-internet-at-tablet");
    };
  }, [isOffline]);

  useEffect(() => {
    if (!isNative) return;

    const initializeNetworkCheck = async () => {
      const status = await Network.getStatus();
      setIsOffline(!status.connected);
    };
    initializeNetworkCheck();

    const networkListener = Network.addListener(
      "networkStatusChange",
      (status) => {
        if (status.connected) {
          setIsOffline(false);
        } else {
          setIsOffline(true);
        }
      },
    );

    return () => {
      networkListener.then((listener) => listener.remove());
    };
  }, [isNative]);

  const handleRetry = async () => {
    setIsChecking(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const status = await Network.getStatus();

    if (status.connected) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);

        await fetch("https://connectivitycheck.gstatic.com/generate_204", {
          method: "GET",
          signal: controller.signal,
          mode: "no-cors",
          cache: "no-store",
        });

        clearTimeout(timeoutId);
        setIsOffline(false);
        message.success("Internet connection restored.");
      } catch (err) {
        setIsOffline(true);
        message.error(
          "Network connected but internet is unreachable. Please check your data connection.",
        );
      }
    } else {
      setIsOffline(true);
      message.error(
        "Still offline. Please check your network setting and try again.",
      );
    }

    setIsChecking(false);
  };

  if (!isNative) return null;

  const userData = getDecryptedUserData();
  const roleName = String(userData?.role?.key || userData?.role?.name || "").toLowerCase();
  
  if (
    window.location.pathname.includes("/face-scanner") || 
    roleName.includes("attendance tablet") || 
    roleName.includes("attendance-tablet")
  ) {
    return null;
  }

  return (
    <Drawer
      placement="bottom"
      closable={false}
      maskClosable={false}
      open={isOffline}
      height="auto"
      zIndex={200000}
      rootClassName="no-internet-drawer-root"
      styles={{
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.45)",
          backdropFilter: "blur(2px)",
          zIndex: 200000,
        },
        wrapper: {
          zIndex: 200000,
        },
        content: {
          borderTopLeftRadius: "28px",
          borderTopRightRadius: "28px",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          maxWidth: "100%",
          margin: "0 auto",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          zIndex: 200000,
        },
        body: {
          padding: 0,
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "40px 24px 44px 24px",
          fontFamily: "'Nunito Sans Regular', sans-serif",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          {webLogoAndImages.noInternetSvg}
        </div>
        <h2
          style={{
            fontFamily: "'Nunito Sans Bold', sans-serif",
            fontSize: "22px",
            fontWeight: 700,
            color: "#000000",
            marginBottom: "12px",
            lineHeight: "1.3",
          }}
        >
          No Internet Connection
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#949494",
            lineHeight: "1.5",
            maxWidth: "300px",
            marginBottom: "32px",
            fontWeight: 400,
          }}
        >
          It looks like you're offline. Connect to the internet and try again.
        </p>
        <Button
          type="primary"
          loading={isChecking}
          onClick={handleRetry}
          style={{
            width: "160px",
            height: "44px",
            backgroundColor: "#231F20",
            borderColor: "#231F20",
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: 700,
            borderRadius: "6px",
            letterSpacing: "0.5px",
          }}
        >
          RETRY
        </Button>
      </div>
    </Drawer>
  );
};
